import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, RefreshCw, Search } from 'lucide-react';
import { Table, toast } from '../../../ui';
import orderService from '../../../services/orderService';
import invoiceService from '../../../services/invoiceService';
import { ordersAPI } from '../services/api';
import { formatDateTime, formatINR } from '../../../utils/format';
import {
  AdminHeader,
  LoadError,
  ORDER_STATES,
  OrderStatusDialog,
  StateBadge,
  allowedTransitions,
  isPaidState,
  lookup,
  resolveOrderState,
} from './orderAdmin';

const QUEUES = [
  { state: '', label: 'All' },
  { state: 'PaymentSubmitted', label: 'Awaiting verification' },
  { state: 'PendingPayment', label: 'Awaiting payment' },
  { state: 'Paid', label: 'Paid, not delivered' },
  { state: 'Fulfilled', label: 'Delivered' },
  { state: 'PaymentFailed', label: 'Payment failed' },
  { state: 'Cancelled', label: 'Cancelled' },
  { state: 'Refunded', label: 'Refunded' },
];

const DELIVERY_LABELS = {
  Pending: 'Delivery pending',
  Failed: 'Delivery failed',
  PartiallyDelivered: 'Partly delivered',
  NeedsAttention: 'Delivery needs attention',
};

// <input type="date"> values are local calendar days.
const startOfDay = value => (value ? new Date(`${value}T00:00:00`) : null);
const endOfDay = value => (value ? new Date(`${value}T23:59:59.999`) : null);
const dayAfter = value => {
  const date = startOfDay(value);
  if (date) date.setDate(date.getDate() + 1);
  return date;
};

const OrderManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stateParam = searchParams.get('state') || '';
  const state = ORDER_STATES.includes(stateParam) ? stateParam : '';

  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [searchTerm, setSearchTerm] = useState(searchInput.trim());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [orders, setOrders] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [statusOrder, setStatusOrder] = useState(null);
  const [exporting, setExporting] = useState(false);
  const requestId = useRef(0);

  // Search runs on the server, a moment after typing stops.
  useEffect(() => {
    const next = searchInput.trim();
    if (next === searchTerm) return undefined;
    const timer = setTimeout(() => {
      setSearchTerm(next);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  const fetchOrders = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    const response = await orderService.getAllOrders(page, pageSize, {
      state,
      searchTerm,
      dateStart: startOfDay(dateFrom),
      dateEnd: endOfDay(dateTo),
    });
    if (id !== requestId.current) return;
    if (response.success && response.data?.data) {
      setOrders(response.data.data.items || []);
      setTotalItems(response.data.data.totalItems || 0);
    } else {
      setOrders([]);
      setTotalItems(0);
      setError(response.error || "Couldn't load orders");
    }
    setLoading(false);
  }, [page, pageSize, state, searchTerm, dateFrom, dateTo]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await ordersAPI.getSummary();
      setSummary(response.data?.data || null);
    } catch {
      setSummary(null); // counts are optional; the list still works without them
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const refresh = () => {
    fetchOrders();
    fetchSummary();
  };

  const selectQueue = next => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('state', next);
    else params.delete('state');
    params.delete('q');
    setSearchParams(params, { replace: true });
    setPage(1);
  };

  const changeDate = setter => event => {
    setter(event.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    selectQueue('');
  };

  const handleExport = async () => {
    setExporting(true);
    const result = await invoiceService.exportCsv(startOfDay(dateFrom), dateTo ? dayAfter(dateTo) : null);
    setExporting(false);
    if (result.success) toast.success('Invoice CSV downloaded');
    else toast.error(result.error);
  };

  const countFor = queueState => {
    if (!summary) return null;
    return queueState ? lookup(summary.byState || {}, queueState) ?? 0 : summary.total;
  };

  const hasFilters = Boolean(state || searchTerm || dateFrom || dateTo);

  const columns = [
    {
      title: 'Order',
      key: 'order',
      width: 210,
      render: (_, record) => (
        <div>
          <Link className="oa-link oa-mono" to={`/admin/orders/${encodeURIComponent(record.orderId)}`}>
            {record.orderId}
          </Link>
          <span className="oa-sub">{formatDateTime(record.createdAt)}</span>
        </div>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 220,
      render: (_, record) => (
        <div>
          <div className="oa-strong">{record.userName || '—'}</div>
          <span className="oa-sub">{record.userEmail || record.userPhone || 'No contact details'}</span>
        </div>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      width: 240,
      render: (_, record) => {
        const first = record.items?.[0];
        if (!first) return <span className="oa-muted">—</span>;
        return (
          <div>
            <div>{first.productName}</div>
            {record.items.length > 1 && <span className="oa-sub">and {record.items.length - 1} more</span>}
          </div>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 110,
      align: 'right',
      render: amount => <span className="oa-num oa-strong">{formatINR(amount)}</span>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_, record) => {
        const orderState = resolveOrderState(record);
        const delivery = isPaidState(orderState) && lookup(DELIVERY_LABELS, record.fulfillment?.status);
        return (
          <div>
            <StateBadge state={orderState} />
            {delivery && <span className="oa-sub">{delivery}</span>}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 190,
      render: (_, record) => {
        const canChange = allowedTransitions(resolveOrderState(record)).length > 0;
        return (
          <div className="oa-inline">
            <Link
              className="pk-btn pk-btn--secondary pk-btn--xs"
              to={`/admin/orders/${encodeURIComponent(record.orderId)}`}
              aria-label={`Open order ${record.orderId}`}
            >
              Open
            </Link>
            <button
              type="button"
              className="pk-btn pk-btn--ghost pk-btn--xs"
              onClick={() => setStatusOrder(record)}
              disabled={!canChange}
              title={canChange ? undefined : 'This order is final'}
              aria-label={`Change status of order ${record.orderId}`}
            >
              Change status
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="oa-page">
      <AdminHeader
        title="Orders"
        sub="Every order with its current state. Customers' UPI payments wait in Awaiting verification until you approve them."
        actions={
          <>
            <button type="button" className="pk-btn pk-btn--ghost pk-btn--sm" onClick={refresh} disabled={loading}>
              <RefreshCw aria-hidden="true" /> Refresh
            </button>
            <button
              type="button"
              className="pk-btn pk-btn--secondary pk-btn--sm"
              onClick={handleExport}
              disabled={exporting}
              aria-busy={exporting || undefined}
              title="GST invoice lines for the selected dates (all dates when none are set)"
            >
              <Download aria-hidden="true" /> Export invoices (CSV)
            </button>
          </>
        }
      />

      <nav className="oa-chips" aria-label="Order queues">
        {QUEUES.map(queue => {
          const count = countFor(queue.state);
          return (
            <button
              key={queue.state || 'all'}
              type="button"
              className="pk-chip"
              aria-pressed={state === queue.state}
              onClick={() => selectQueue(queue.state)}
            >
              {queue.label}
              {count !== null && count !== undefined && <span className="oa-count">{count}</span>}
            </button>
          );
        })}
      </nav>

      <div className="pk-card oa-panel">
        <div className="oa-toolbar">
          <div className="pk-field oa-grow">
            <label className="pk-label" htmlFor="orders-search">
              Search
            </label>
            <div className="pk-input-icon">
              <Search aria-hidden="true" />
              <input
                id="orders-search"
                type="search"
                className="pk-input oa-sm"
                placeholder="Order ID, invoice number, customer name, email or phone"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className="pk-field">
            <label className="pk-label" htmlFor="orders-from">
              From
            </label>
            <input
              id="orders-from"
              type="date"
              className="pk-input oa-sm"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={changeDate(setDateFrom)}
            />
          </div>
          <div className="pk-field">
            <label className="pk-label" htmlFor="orders-to">
              To
            </label>
            <input
              id="orders-to"
              type="date"
              className="pk-input oa-sm"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={changeDate(setDateTo)}
            />
          </div>
          <button type="button" className="pk-btn pk-btn--ghost pk-btn--sm" onClick={clearFilters} disabled={!hasFilters}>
            Clear filters
          </button>
        </div>
      </div>

      {error ? (
        <LoadError title="Couldn't load orders" message={error} onRetry={fetchOrders} />
      ) : (
        <div className="pk-card oa-panel oa-panel--flush">
          <div className="oa-panel__head">
            <h2 className="oa-panel__title">
              {state ? QUEUES.find(q => q.state === state)?.label : 'All orders'}
            </h2>
            <span className="oa-small oa-muted" aria-live="polite">
              {loading ? 'Loading…' : `${totalItems} ${totalItems === 1 ? 'order' : 'orders'}`}
            </span>
          </div>
          <Table
            dataSource={orders}
            columns={columns}
            loading={loading}
            rowKey="orderId"
            scroll={{ x: 1100 }}
            emptyText={hasFilters ? 'No orders match these filters.' : 'No orders yet.'}
            pagination={{
              total: totalItems,
              current: page,
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: total => `${total} orders`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              },
            }}
          />
        </div>
      )}

      <OrderStatusDialog
        open={!!statusOrder}
        order={statusOrder}
        onClose={() => setStatusOrder(null)}
        onDone={refresh}
      />
    </div>
  );
};

export default OrderManagement;
